import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Image, Icon, Loader, Button, Grid, Card, Form, Checkbox } from "semantic-ui-react";
import BCDashboard from "./ui/BCDashboard";
import BCProfileInfo from "./BCProfileInfo";
import BCButton from "./ui/BCButton";
import BCColorPicker from "./ui/BCColorPicker";
import BCDragAndDrop from "./ui/BCDragAndDrop";
import BCMessage from "./ui/BCMessage";
import ProfileUploadIcon from "../assets/bc-avatar-builder-green.svg";
import { getProfile, updateProfile, updateProfileAvatar, updateBrokerLogo, getProfileAsset, createProfile, getW9Url, download, createSubdomainUrl, deleteSubdomain, deleteProfileAvatar, deleteBrokerLogo, getSimilarBrokerageNameList } from "../api/BrokerApi";
import { getMockProfile, updateMockProfile, updateMockProfileAvatar, updateMockBrokerLogo, getMockProfileAsset, createMockProfile, getMockW9Url, downloadMock, createMockSubdomainUrl, deleteMockSubdomain, deleteMockProfileAvatar, deleteMockBrokerLogo, getMockSimilarBrokerageNameList } from "../api/mock/MockBrokerApi";
import { createErrorResponse, createFile, guid, handleFormChange, isValidPermission, validateObj, handleErrorResponse, deepCopy, handleSpaFwEventLog } from "../Helpers";
import "../styles/profile.scss";
import "../styles/modal.scss";
import "../styles/main.scss";
import BCFormInput from "./ui/BCFormInput";
import { css } from "@emotion/css";
import { GlobalConstants, Personas } from "./constants/GlobalConstants";
import { FeatureFlags } from "./constants/LocalServerConstants";
import { getIsWhiteLabeled, whoAmI } from "../helpers/PermissionHelper";
import BCModal from "./ui/BCModal";
import { IBrokerage } from "./interfaces/GetMyRate";
import BCValidationMessage from "./ui/BCValidationMessage";

const BCProfile = ({
  isGreaterThanMobile, 
  isGreaterThanTablet, 
  isSidebar, 
  isMessage, 
  setIsMessage,
  errorResponse,
  setErrorResponse,
  setSuccessMessage, 
  user, 
  setUser, 
  proxyUser,
  handleProxyUser,
  setIsW9MessageVisible,
  permissions,
  setIsCreateNewUser,
  brokerColor,
  setTenantLogo,
  setNewAvatarFile,
  setIsUserProfileEdit,
  setIsUserProfileUpdate,
  setSubdomain
}) => {
  const [initialProfile, setInitialProfile] = useState({} as any);
  const [profile, setProfile] = useState({} as any);
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [invalidProps, setInvalidProps] = useState([] as any);
  const [avatarFile, setAvatarFile] = React.useState(undefined as any);
  const [avatarFileUrl, setAvatarFileUrl] = React.useState(undefined as any);
  const [logoFileUrl, setLogoFileUrl] = React.useState(undefined as any);
  const [isAvatarDrag, setIsAvatarDrag] = React.useState(false);
  const [isLogoDrag, setIsLogoDrag] = React.useState(false);
  const [downloadURL, setDownloadURL]  = useState(undefined as any);
  const [color, setColor] = useState();
  const [hexValue, setHexValue] = useState();
  const [completedW9, setCompletedW9] = React.useState(false as boolean);
  const [isSubdomainURLWarningMessage, setIsSubdomainURLWarningMessage] = useState(false as boolean);
  const [isSubdomainURLSuccessMessage, setIsSubdomainURLSuccessMessage] = useState("" as string);
  const [suggestedSubdomainNames, setSuggestedSubdomainNames] = useState([] as any);
  const [isUseWhiteLabeled, setIsUseWhiteLabeled] = useState(false as boolean);
  const [isBrokerageModalOpen, setIsBrokerageModalOpen] = useState(false as boolean);
  const [similarBrokerageList, setSimilarBrokerageList] = useState([] as any[]);
  const [brokerageInfo, setBrokerageInfo] = useState({} as any);
  const [newBrokerageId, setNewBrokerageId] = useState("" as string);
  const [newBrokerageName, setNewBrokerageName] = useState("" as string);
  const [isNewBrokerage, setIsNewBrokerage] = useState(true as boolean);
  const [isBrokerageCreateLoading, setIsBrokerageCreateLoading] = useState(false as boolean);
  const [newProfileObj, setNewProfileObj] = useState({} as any);
  const avatarFileRef = useRef(null as any);
  const brokerLogoFileRef = useRef(null as any);
  const exportRef = useRef<HTMLElement>(null);
  const W9Asset = "9B8C2C2C-57AB-48D6-A35B-3CCCB13E71B7";
  const brokerProfilePictureOverrideCSS = css`
    border-radius: 10px;
    padding: 10px 10px 10px 10px;
    border: dashed 1px ${brokerColor ? brokerColor : "black"}!important;
    height: 100%;
  `;
  const brokerProfileContainerOverrideCSS = css`
    color: ${brokerColor ? brokerColor : "black"}!important;
    font-size: 12px;
    font-family: $fontFamily;
    display: flex;
    flex-direction: column;
    margin: 0;
    position: relative;
    top: 50%;
    -ms-transform: translateY(-50%);
    transform: translateY(-50%);
  `;
  const persona = whoAmI(user, proxyUser);
  const isAnyNWLPersona = [
    Personas.SYSTEM_ADMIN,
    Personas.SALES_ADMIN,
    Personas.BROKER_ADMIN,
    Personas.LOAN_OFFICER,
    Personas.BROKER,
    Personas.BRM,
    Personas.NWL_GUARANTOR,
    Personas.SALES_ASSISTANT
  ].indexOf(persona) > -1; 

  const isTwoColumnLayout = (!getIsWhiteLabeled() && !(!getIsWhiteLabeled() && persona === Personas.UNKNOWN));
  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleHexValue = (hex) => {
    setHexValue(hex);
    profile.brokerColor = hex;
    setProfile(profile);
  };

  const onFileChange = (e, type) => {
    const changedFile = e.target.files[0];
    if (changedFile) {
      const fileUrl = URL.createObjectURL(e.target.files[0]);
      switch(type) {
        case "profile":
          if (!profile.avatarAssetId) {
            profile.avatarAssetId = guid();
            setProfile({...profile});
          }

          if (isSidebar) {
            handleAvatarChangedAsync(e.target.files[0]);
          }
          
          setAvatarFile(e.target.files[0]);
          setAvatarFileUrl(fileUrl);
          break;
        case "logo":
          if (!profile.brokerLogoAssetId) {
            profile.brokerLogoAssetId = guid();
            setProfile({...profile});
          }
          handleLogoChangedAsync(e.target.files[0]);
          setTenantLogo(e.target.files[0]);
          setLogoFileUrl(fileUrl);
          break;
        default:
          createErrorResponse("Warning", "Action Not Found");
          break;
      }
    }
  };

  const handleUploadClick = (type) => {
    switch(type) {
      case "profile":
        avatarFileRef.current.click();
        break;
      case "logo":
        brokerLogoFileRef.current.click();
        break;
      default:
        createErrorResponse("Warning", "Action Not Found");
        break;
    }
  };

  const convertToProfilePublish = (profile) => {
    return {
      "id": profile.id ? profile.id : guid(),
      "physicalStreet": profile.address,
      "physicalZipCode": profile.zip,
      "physicalStateName": profile.state,
      "physicalCityName": profile.city,
      "physicalCountyName": profile.county,
      "mailingStreet": profile.address,
      "mailingZipCode": profile.zip,
      "mailingStateName": profile.state,
      "mailingCityName": profile.city,
      "mailingCountyName": profile.county,
      "email": profile.email,
      "firstName": profile.firstName,
      "lastName": profile.lastName,
      "mobilePhone": profile.phone,
      "avatarAssetId": profile.avatarAssetId ? profile.avatarAssetId : undefined,
      "preferredLoanOfficerOrganizationContactId" : profile.preferredLoanOfficerOrganizationContactId !== "" ? profile.preferredLoanOfficerOrganizationContactId : undefined,
      "organizations": [
          {
              "id": profile.organizationId ? profile.organizationId : guid(),
              "name": profile.company,
              "entitySubTypeId": profile.entitySubTypeId,
              "mailingCityName": profile.city,
              "mailingStateName": profile.state,
              "mailingStreet": profile.address,
              "mailingZipCode": profile.zip,
              "mailingCountyName": profile.county,
              "physicalCityName": profile.city,
              "physicalStateName": profile.state,
              "physicalStreet": profile.address,
              "physicalZipCode": profile.zip,
              "physicalCountyName": profile.county,
              "themeColor": profile.brokerColor ? profile.brokerColor : undefined,
              "logoAssetId": profile.brokerLogoAssetId ? profile.brokerLogoAssetId : undefined,
              "useWhitelabelPreferences": profile.useWhitelabelPreferences
          }
      ],
      "organizationContactId": profile.organizationContactId
    };
  };

  const handleDownloadW9 = async () => {
    try {
      setIsLoading(true);
      const response = FeatureFlags.IS_MOCK ? await downloadMock(W9Asset): await download(W9Asset, proxyUser.id);
        if (response && response.ok) {
          setIsLoading(false);
          setIsMessage(false);
          const data = await response.blob();
          const exportQuote = await createFile(data, "gmr-quote", data.type);
          setDownloadURL(URL.createObjectURL(exportQuote));
          if (exportRef.current) {
            exportRef.current.click();
          }
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
          setIsLoading(false);
        }
    } 
    catch (error) {
      setIsLoading(true);
      setIsMessage(false);
    }
  };

  const handleCreateProfile = async () => {
    setInvalidProps([]);
    const notValidProperties = validateObj(brokerageInfo, ["organizationName", "organizationId"]);
    if (notValidProperties.length === 0) {
      const profileObj = deepCopy(newProfileObj);
      if (isNewBrokerage) {
        delete profileObj.organizationId;
      }
      else {
        delete profileObj.organizations;
      }
      
      setIsBrokerageCreateLoading(true);
      await handleCreateProfileAsync(profileObj);
      if (avatarFile) {
        await handleAvatarChangedAsync(avatarFile);
      }

      setIsBrokerageModalOpen(false);
      setIsBrokerageCreateLoading(false);
    }
    else {
      setTimeout(() => { 
        setInvalidProps(notValidProperties);
      }, 50);
    }
  };

  const handleCreateProfileAsync = async (profileObj) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await createMockProfile(profileObj) : await createProfile(profileObj, proxyUser.id);
      if (response && response.ok) {
        setIsMessage(false);
        setSuccessMessage("Your profile has been created.");
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
        setIsCreateNewUser(true);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  const handleSelectBrokerage = (value, field) => {
    handleFormChange(value, field, brokerageInfo, setBrokerageInfo, GlobalConstants.InputTypes.STRING);
    const selectedBrokerage = similarBrokerageList.find(x => x.id === value);
    const brokerageKey = (name: keyof IBrokerage) : string => name;
    if (selectedBrokerage) {
      Object.entries(selectedBrokerage).forEach(([key, value]) => {
        if (key === brokerageKey("name")) {
          brokerageInfo.organizationName = value;
        } 
        else if (key === brokerageKey("id")) {
          brokerageInfo.organizationId = value;
          newProfileObj.organizationId = value;
          setNewProfileObj(newProfileObj);
        }
        else {
          brokerageInfo[key] = value;
        }
      });

      setBrokerageInfo({...brokerageInfo});
      setIsNewBrokerage(false);
    }
    else {
      setBrokerageInfo({ organizationName: newBrokerageName, organizationId: newBrokerageId });
      setIsNewBrokerage(true);
    }
  };

  const handleSubmitBySite = () => {
    if ((!getIsWhiteLabeled() && (isAnyNWLPersona && profile.id) || 
    (persona === Personas.UNKNOWN && profile && !profile.id)) || 
    getIsWhiteLabeled() && persona === Personas.WL_GUARANTOR) {
      handleUpdateProfile();
    }
    else {
      handleSpaFwEventLog("Caught error while trying to create or update profile", setIsMessage, setErrorResponse);
    }
  };

  const handleUpdateProfile = async () => {
    setIsLoading(true);
    setInvalidProps([]);
    const notValidProperties = validateObj(profile, ["firstName", "lastName", "email", "phone", "company", "entitySubTypeId", "address", "city", "state", "zip"]);
    if (notValidProperties.length === 0) {
      const profileObj = convertToProfilePublish(profile);
      if (profile.id) {
        await handleUpdateProfileAsync(profileObj);
        if (proxyUser.id) {
          handleProxyUser({...proxyUser, 
            preferredLoanOfficerOrganizationContactId: 
            profileObj.preferredLoanOfficerOrganizationContactId, 
            organizationContactId: profileObj.organizationContactId
          });
        }
      }
      else {
        await handleGetBrokerageNameListAsync(profile.company);
        setNewProfileObj(profileObj);
        if (profileObj.organizations && profileObj.organizations[0]) {
          setNewBrokerageId(profileObj.organizations[0].id);
          setNewBrokerageName(profileObj.organizations[0].name);
        }

        setIsBrokerageModalOpen(true);
      }

      setIsLoading(false);
    }
    else {
      createErrorResponse("Info", "Not Valid Properties", notValidProperties);
      setTimeout(() => { 
        setInvalidProps(notValidProperties);
      }, 50);
    }

    setIsLoading(false);
    setIsUserProfileUpdate(true);
  };

  const handleDrop = (files, type) => {
    if (files.length > 0 && (files[0].type === "image/jpeg" || files[0].type === "image/png")) {
      const fileUrl = URL.createObjectURL(files[0]);
      switch(type) {
        case "profile":
          if (!profile.avatarAssetId) {
            profile.avatarAssetId = guid();
            setProfile({...profile});
          }

          if (isSidebar) {
            handleAvatarChangedAsync(files[0]);
          }

          setAvatarFile(files[0]);
          setAvatarFileUrl(fileUrl);
          break;
        case "logo":
          if (!profile.brokerLogoAssetId) {
            profile.brokerLogoAssetId = guid();
            setProfile({...profile});
          }
          handleLogoChangedAsync(files[0]);
          setTenantLogo(files[0]);
          setLogoFileUrl(fileUrl);
          break;
        default:
          createErrorResponse("Warning", "handleDrop missing type error");
          break;
      }
    }
    else {
      createErrorResponse("Warning", "handleDrop event error");
    }
  };

  const handleUpdateSubdomainUrlAsync = async () => {
    if (profile && profile.subdomain && profile.organization && profile.organization.subdomain) {
      const updatedSubdomain = profile.subdomain;
      const isDeleteSubdomainSuccess = await handleDeleteSubdomainUrlAsync(profile.organization.subdomain);
      if (isDeleteSubdomainSuccess) {
        await handleCreateSubdomainURLAsync(updatedSubdomain);
      }
    }
  };

  const handleUpdateProfileAsync = async (profileObj, successMessage = "Your profile has been updated.") => {
    try {
      const response = FeatureFlags.IS_MOCK ? await updateMockProfile(profileObj) : await updateProfile(profileObj, proxyUser.id);
      if (response && response.ok) {
        setIsMessage(false);
        setSuccessMessage(successMessage);
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  const handleW9URLAsync = useCallback(async () => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockW9Url(user.id) : await getW9Url(proxyUser.id, window.location.toString()) as any;
      if (response && response.ok) {
        const getW9UrlObj = await response.json();
        if (getW9UrlObj) {
          const win = window.location.replace(getW9UrlObj) as any;
          win.focus();
          setIsW9MessageVisible(true);
        }
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
   
  }, [user, proxyUser, setIsMessage, setErrorResponse, setIsW9MessageVisible]);

  const handleDeleteSubdomainUrlAsync = async (subdomain) => {
    setIsLoading(true);
    try {
      const response = FeatureFlags.IS_MOCK ? await deleteMockSubdomain(subdomain) : await deleteSubdomain(subdomain, proxyUser.id) as any;
      if (response && response.ok) {
        setIsLoading(false);
        profile.subdomain = "";
        if (profile.organization) {
          profile.organization.subdomain = undefined;
        }
        
        setIsSubdomainURLSuccessMessage("Your Subdomain was deleted.");
        setProfile({...profile});
        return true;
      }
      else {
        setIsSubdomainURLSuccessMessage("");
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsSubdomainURLSuccessMessage("");
      setIsMessage(false);
    }

    setIsLoading(false);
    return false;
  };

  const handleCreateSubdomainURLAsync = async (subdomain) => {
    try {
      setIsLoading(true);
      const response = FeatureFlags.IS_MOCK ? await createMockSubdomainUrl(subdomain) : await createSubdomainUrl(subdomain, proxyUser.id) as any;
      if (response && response.ok) {
        setIsLoading(false);
        setSubdomain(subdomain);
        setIsSubdomainURLSuccessMessage("Your Subdomain was created.");
        setIsSubdomainURLWarningMessage(false);
        profile.subdomain = subdomain;
        profile.organization = {
          subdomain: profile.subdomain
        };
        setProfile({...profile});
      }
      else if(response.status === 409){
        setIsLoading(false);
        setIsSubdomainURLSuccessMessage("");
        setIsSubdomainURLWarningMessage(true);
        const responseObj = await response.json() as any;
        setSuggestedSubdomainNames((responseObj && responseObj.suggestedNames) && responseObj.suggestedNames);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
        setIsSubdomainURLSuccessMessage("");
        setIsSubdomainURLWarningMessage(true);
        setIsLoading(false);
      }
    }
    catch (error) {
      setIsMessage(false);
      setIsSubdomainURLSuccessMessage("");
      setIsSubdomainURLWarningMessage(true);   
      setIsLoading(false);
    }
  };

  const handleDeleteProfileAvatar = async (assetId) => {
    try {
      setIsLoading(true);
      const response = FeatureFlags.IS_MOCK ? await deleteMockProfileAvatar(assetId) : await deleteProfileAvatar(assetId, proxyUser.id) as any;
      if (response && response.ok) {
        setAvatarFileUrl(undefined);
        user.avatar = undefined;
        setUser({...user});
        profile.avatarAssetId = undefined;
        setProfile({...profile});
        setSuccessMessage("Your profile avatar has been deleted.");
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
        setIsLoading(false);
      }
    }
    catch (error) {
      setIsMessage(false);
    }

    setIsLoading(false);
  };

  const handleDeleteBrokerLogo = async (assetId) => {
    try {
      setIsLoading(true);
      const response = FeatureFlags.IS_MOCK ? await deleteMockBrokerLogo(assetId) : await deleteBrokerLogo(assetId, proxyUser.id) as any;
      if (response && response.ok) {
        setLogoFileUrl(undefined);
        profile.brokerLogoAssetId = undefined;
        setProfile({...profile});
        setSuccessMessage("Your whitelabeled logo has been deleted.");
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
        setIsMessage(false);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
        setIsLoading(false);
      }
    }
    catch (error) {
      setIsMessage(false);
    }

    setIsLoading(false);
  };

  const handleUseWhiteLabel = async () => {
    setIsUseWhiteLabeled(!isUseWhiteLabeled);
    profile.useWhitelabelPreferences = !isUseWhiteLabeled;
    setProfile({...profile});
    initialProfile.useWhitelabelPreferences = !isUseWhiteLabeled;
    const profileObj = convertToProfilePublish(initialProfile);
    await handleUpdateProfileAsync(profileObj, "Your whitelabeled preference has been updated.");
  };

  const handleUpdateColorPicker = async () => {
    initialProfile.brokerColor = hexValue;
    const profileObj = convertToProfilePublish(initialProfile);
    await handleUpdateProfileAsync(profileObj, "Your whitelabeled color has been updated.");
  };

  const handleLogoChangedAsync = async (logoFile) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await updateMockBrokerLogo(logoFile, profile.brokerLogoAssetId) : await updateBrokerLogo(logoFile, profile.brokerLogoAssetId, proxyUser.id);
      if (response && response.ok) {
        setSuccessMessage("Your whitelabeled logo has been updated.");
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
        setIsMessage(false);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  const handleAvatarChangedAsync = async (avatarFile) => {
    try {
      const assetId = profile.avatarAssetId ? profile.avatarAssetId : guid();
      const response = FeatureFlags.IS_MOCK ? await updateMockProfileAvatar(avatarFile, assetId) : await updateProfileAvatar(avatarFile, assetId, proxyUser.id);
      if (response && response.ok) {
        setSuccessMessage("Your profile avatar has been updated.");
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
        setIsMessage(false);
        setNewAvatarFile(avatarFile);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  const handleGetBrokerageNameListAsync = async (name) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockSimilarBrokerageNameList() : await getSimilarBrokerageNameList(encodeURI(name), proxyUser.id, true) as any;
      if (response && response.ok) {
        setIsMessage(false);
        const companyList = await response.json();
        setSimilarBrokerageList([...companyList]);
        return true;
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }

    return false;
  };

  useEffect(() => {
    const handeGetProfileAssetAsync = async (id, type) => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockProfileAsset(id) : await getProfileAsset(id, proxyUser.id) as any;
        if (response && response.ok) {
          const data = await response.blob();
          if (type === "avatar") {
            setAvatarFileUrl(user.avatar);
          }
          else if (type === "broker logo") {
            const brokerLogoFile = await createFile(data, "broker-logo", data.type);
            setTenantLogo(brokerLogoFile);
            setLogoFileUrl(URL.createObjectURL(brokerLogoFile));
          }
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        setIsMessage(false);
      }
  
      setIsLoading(false);
    };

    const getProfileAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockProfile() : await getProfile(proxyUser.id);
        if (response && response.ok) {
          const profile = await response.json() as any;
          let profileEmail = user.email ? user.email : profile.email ? profile.email : "";
          let profilePhone = user.mobilePhone ? user.mobilePhone : profile.mobilePhone;
          if (proxyUser.id) {
            profileEmail = profile.email;
            profilePhone = profile.mobilePhone;
          }

          if (profile.id) {
            const profileObj = {
              id: profile.id,
              firstName: profile.firstName,
              lastName: profile.lastName,
              email: profileEmail,
              phone: profilePhone,
              organizationId: profile.organization && profile.organization.id,
              entitySubTypeId: (profile.organization && profile.organization.entitySubTypeId) && profile.organization.entitySubTypeId,
              company: (profile.organization && profile.organization.name) && profile.organization.name,
              address: profile.physicalStreet,
              zip: profile.physicalZipCode,
              state: profile.physicalStateName,
              city: profile.physicalCityName,
              brokerColor: (profile.organization && profile.organization.themeColor) && profile.organization.themeColor,
              avatarAssetId: profile.avatarAssetId,
              brokerLogoAssetId: (profile.organization && profile.organization.logoAssetId) && profile.organization.logoAssetId,
              w9Status: profile.w9Status,
              organization: profile.organization,
              subdomain: (profile.organization && profile.organization.subdomain),
              preferredLoanOfficerOrganizationContactId : profile.preferredLoanOfficerOrganizationContactId,
              isPhoneEditable: profilePhone === "" || !profilePhone,
              useWhitelabelPreferences: (profile.organization && profile.organization.useWhitelabelPreferences) ? true : false,
            };
            
            setProfile(profileObj);
            setInitialProfile(profileObj);
            if (profile.organization && profile.organization.useWhitelabelPreferences) {
              setIsUseWhiteLabeled(true);
            }

            if (profile.organization && profile.organization.subdomain) {
              setSubdomain(profile.organization.subdomain);
            }
            
            if (profile.avatarAssetId) {
              await handeGetProfileAssetAsync(profile.avatarAssetId, "avatar");
            }

            if (profile.organization && profile.organization.logoAssetId) {
              await handeGetProfileAssetAsync(profile.organization.logoAssetId, "broker logo");
            }
            
            setCompletedW9(profile.isW9Completed);  
          }
          else {
            if (user && user.email && user.mobilePhone) {
              setProfile({ email: user.email, phone: user.mobilePhone });
            }
          }
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        setIsMessage(false);
        handleError();
      }

      setIsLoading(false);
    };
    
    getProfileAsync();
  }, [setIsMessage, setErrorResponse, handleError, setHexValue, setTenantLogo, setProfile, user, proxyUser, setCompletedW9, profile.isW9Completed, setSubdomain]);

  useEffect(() => {
    setIsUserProfileEdit(false);
    setIsUserProfileUpdate(false);
  }, [setIsUserProfileEdit, setIsUserProfileUpdate]);

  const UploadBox = ({type}) => {
    const imageRender = (type) => {
      if (type === "profile") {
        return (
          <Image 
            className={`preview-upload ${!avatarFileUrl && "upload-preview-default"}`} 
            src={(avatarFileUrl) ? avatarFileUrl : getIsWhiteLabeled() ? void 0: ProfileUploadIcon} 
          />
        );
      }
      else if (type === "logo") {
        return (
          <React.Fragment>
            {logoFileUrl ? <Image 
              className="preview-upload-square"
              src={logoFileUrl}
              disabled={!isUseWhiteLabeled}
            /> : <Image className={"preview-upload upload-preview-default"}>
              <Icon name="briefcase" className="upload-logo-default-icon" />
            </Image>}
          </React.Fragment>
        );
      }
    };
    
    return (
      <div className={(brokerColor && getIsWhiteLabeled()) ? brokerProfileContainerOverrideCSS : "upload-container vertical-center"}>
        {imageRender(type)}
        <div className="upload-text upload-text-first"><b>Drag your {type === "profile" ? "profile picture" : "logo"} here or browse</b></div>
        <div className="upload-text">JPEGs and PNGs accepted</div>
      </div>
    );
  };

  const ProfileAvatarAndW9 = () => {
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <Card fluid>
              <Card.Header className="dashboard-card-title">
                <div className="profile-avatar-title-container">
                  <div>Profile Image</div>
                  {user.avatar && <div className="profile-avatar-clear" onClick={() => handleDeleteProfileAvatar(profile.avatarAssetId)}>Clear</div>}
                </div>
              </Card.Header>
              <Card.Content className="dashboard-card-content">
                <Card.Description id="profile-avatar-description" className="dashboard-card-description">
                  <BCDragAndDrop 
                    handleDropEvent={(files) => handleDrop(files, "profile")} 
                    setIsDragging={setIsAvatarDrag} 
                    onClick={() => handleUploadClick("profile")}
                  >
                    <div className={(brokerColor || getIsWhiteLabeled()) ? brokerProfilePictureOverrideCSS :`upload-border ${isAvatarDrag && "upload-drag"}`}>
                      <UploadBox type="profile" />
                    </div>
                  </BCDragAndDrop>
                </Card.Description>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
        {persona === Personas.BROKER && <Grid.Row className="dashboard-row-layered">
          <Grid.Column>
            <Card fluid>
              <Card.Header className="dashboard-card-title">W9 Form</Card.Header>
              <Card.Content className="dashboard-card-content">
                <Card.Description id="profile-w9-description" className="dashboard-card-description">
                {completedW9 ? 
                  <div className="landing-page-empty-container-w9 full-height">
                    <div className="landing-page-empty-text">You have completed your W9. Please Click to Download</div>
                    <div>
                      <a className={"export-link"} href={downloadURL} download ref={exportRef as any}>0</a>
                      <BCButton brokerColor={brokerColor} handleOnClick={() => handleDownloadW9()} ><div>Download</div></BCButton>
                    </div>
                  </div> : 
                  <div className="landing-page-empty-container-w9 full-height">
                    <div className="landing-page-empty-text">It looks like you haven't completed your W9. Please click here to complete it.</div>
                    <div className="">
                      <BCButton brokerColor={brokerColor} handleOnClick={() => handleW9URLAsync()}><div>Complete</div></BCButton>
                    </div>
                  </div>}
                </Card.Description>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>}
      </Grid>
    );
  };

  const baseProfileLayout = [
    {
      title: "Profile Information",
      size: isTwoColumnLayout ? 5 : 8,
      content: <React.Fragment>
        <BCProfileInfo
          persona={persona}
          profile={profile} 
          setProfile={setProfile} 
          invalidProps={invalidProps} 
          permissions={permissions} 
          proxyUser={proxyUser}
        />
        <BCButton type="submit" handleOnClick={handleSubmitBySite} brokerColor={brokerColor} className="profile-button">{profile.id ? "Update" : "Create"}</BCButton>
      </React.Fragment>
    }
  ];

  const WhiteLabelPreferencesLayout = {
    title: "Whitelabel Preferences",
    size: 6,
    content: <React.Fragment>
      <div className="profile-wl-preferences-container full-height">
        <div id="profile-wl-toggle">
          <div className="profile-toggle">
            <Form>
              <BCFormInput 
                labelClass="gmr-background-label"
                label="Would you like to use your branding for your clients’ experience?"
                tooltip={<React.Fragment>
                  <span>If you select to whitelabel your site, your clients will experience a website themed with your branding, including your company color, logo, and a customized subdomain for your portal website address. If you choose not to whitelabel your site, your clients' experience will include our default coloring, portal address and no logo.</span>
                  <p></p>
                  <span>Regardless of the experience you chose for your clients, they will see contact information for Construction Loan Services II so that we may help them on your behalf should they have any questions or technical issues, but your clients will not see any reference to Builders Capital."</span>
                </React.Fragment>}
              />
            </Form>
            <Checkbox 
              className="view-toggle" 
              toggle
              onChange={() => handleUseWhiteLabel()}
              defaultChecked={isUseWhiteLabeled}
            />
          </div>
        </div>
        <div>
        {isValidPermission(permissions, "BrokerBorrowerApplication", "read") && <div className="landing-page-empty-container-w9 full-height">
          <Form>
            <BCFormInput 
              labelClass="gmr-background-label profile-subdomain-label"
              label="Your Subdomain:"
            />
          </Form>
          <div id="profile-subdomain-input-container">
            <BCFormInput 
              inputClass="profile-subdomain-input"
              type="input"
              placeholder="portal"
              obj={profile}
              field="subdomain"
              inputType="text"
              unitType=".ServiceYourLoan.com"
              unitTypeClass="profile-subdomain-unit-class"
              handleFormChange={(value, field) => handleFormChange(value, field, profile, setProfile, GlobalConstants.InputTypes.SUBDOMAIN, undefined, 250)}
              disabled={!isUseWhiteLabeled}
            />
            {(profile.organization && profile.organization.subdomain && profile.organization.subdomain === profile.subdomain) ? <Button 
              color={"red"}
              onClick={() => handleDeleteSubdomainUrlAsync(profile.subdomain)}
              disabled={!isUseWhiteLabeled}
            >
              Delete
            </Button> : (profile.organization && profile.organization.subdomain) ? <BCButton 
              brokerColor={brokerColor} 
              handleOnClick={() => handleUpdateSubdomainUrlAsync()}
              disabled={!profile.subdomain || profile.subdomain === "" || !isUseWhiteLabeled}
            >
              Update
            </BCButton> : <BCButton 
              brokerColor={brokerColor} 
              handleOnClick={() => handleCreateSubdomainURLAsync(profile.subdomain)}
              disabled={!profile.subdomain || profile.subdomain === "" || !isUseWhiteLabeled}
            >
              Create
            </BCButton>}
          </div>
          </div>}
        </div>
        <div id="profile-wl-broker-logo">
          <Form>
            <BCFormInput 
              labelClass="gmr-background-label"
              label={!isUseWhiteLabeled && "Broker Logo:"}
              actionLabelClass="sub-action-label"
              actionLabel="Broker Logo:"
              actionText={(profile.brokerLogoAssetId && isUseWhiteLabeled) ? "Clear" : ""}
              labelAction={() => handleDeleteBrokerLogo(profile.brokerLogoAssetId)}
              tooltip="Optimal logo size is 132 x 132 pixels."
            />
          </Form>
          {isValidPermission(permissions, "BrokerBorrowerApplication", "read") && <BCDragAndDrop 
            handleDropEvent={(files) => isUseWhiteLabeled ? handleDrop(files, "logo"): void 0} 
            setIsDragging={setIsLogoDrag} 
            onClick={() => isUseWhiteLabeled ? handleUploadClick("logo") : void 0}
          >
            <div className={`upload-border ${isLogoDrag && "upload-drag"}`}>
              <UploadBox type="logo" />
            </div>
          </BCDragAndDrop>}
        </div>
        <div id="profile-wl-color-picker">
          {isValidPermission(permissions, "BrokerBorrowerApplication", "read") && <div className="broker-theme-color-container">
            <Form>
              <BCFormInput 
                labelClass="gmr-background-label" 
                label="Broker Theme Color:" 
                tooltip="If you have a specific logo color, enter it here as the hexidecimal code like #323232"
              />
            </Form>
            <BCColorPicker
              initialHex={(profile && profile.brokerColor) ? profile.brokerColor : brokerColor}
              color={color}
              setColor={setColor}
              hexValue={hexValue}
              setHexValue={handleHexValue}
              disabled={!isUseWhiteLabeled}
              brokerColor={brokerColor}
              handleOnClick={() => isUseWhiteLabeled ? handleUpdateColorPicker() : void 0}
            />
          </div>}
        </div>
      </div>
    </React.Fragment>
  };

  const ProfileAvatarAndW9Layout = {
    custom: <ProfileAvatarAndW9 />,
    size: isTwoColumnLayout ? 5 : 8
  };

  const profileLayout = [] as any;
  const combinedLayouts = baseProfileLayout as any;
  if (persona === Personas.BROKER) {
    combinedLayouts.push(WhiteLabelPreferencesLayout);
  }

  combinedLayouts.push(ProfileAvatarAndW9Layout);
  profileLayout.push(combinedLayouts);
  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "hide-sidebar" : "sidebar"}>
      {isLoading ? <Loader className="" active inline="centered" size={"big"} /> : <div>
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => setIsMessage(false)}
          errorResponse={errorResponse}
        />
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isSubdomainURLSuccessMessage !== ""}
          transitionOnMount
          styles="full-width"
          negative
          color="green"
          onDismiss={() => setIsSubdomainURLSuccessMessage("")}
          header={"Success!"}
          text={isSubdomainURLSuccessMessage}
        />
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isSubdomainURLWarningMessage}
          transitionOnMount
          styles="full-width"
          negative
          color="yellow"
          onDismiss={() => setIsSubdomainURLWarningMessage(false)}
          header={"Oops!"}
          text={suggestedSubdomainNames ? "It looks like this name is taken, but here are some suggestions: " + suggestedSubdomainNames.toString(): "It looks like this name is taken, please try another name."}
        />
        <BCModal
          isModalOpen={isBrokerageModalOpen}
          closeOnlyByButton={true}
          onClose={() => setIsBrokerageModalOpen(false)}
          onOpen={() => setIsBrokerageModalOpen(true)}
          header="Select Brokerage"
          content={<>
            <BCFormInput
              labelClass="form-input-label"
              type="radio"
              radioClass="primary-modal-radio"
              inputKey="brokerage"
              inputArray={[...similarBrokerageList, { id: newBrokerageId, name: `None of the above, use "${newBrokerageName}"`}]}
              label="We found a few companies in our system that could be a match. Please select the best match."
              obj={brokerageInfo}
              field="organizationId"
              valueField="id"
              handleFormChange={(value, field) => handleSelectBrokerage(value, field)}
              required
            />
            <BCValidationMessage invalidProps={invalidProps} prop="organizationId" />
          </>}
          actions={<Grid>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Button icon fluid onClick={() => setIsBrokerageModalOpen(false)}>
                  <div className="gmr-button-container">
                    <div className="gmr-button-text">Close</div>
                  </div>
                </Button>
              </Grid.Column>
              <Grid.Column>
                <Button
                  className="bp-button"
                  fluid
                  onClick={() => handleCreateProfile()}
                  loading={isBrokerageCreateLoading}
                >
                  <div className="gmr-button-container">
                    <div className="gmr-button-text">Create</div>
                  </div>
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>}
        />
        {profile && <React.Fragment>
          <input
            className="hidden-file-input"
            type="file"
            ref={avatarFileRef}
            onChange={(e) => onFileChange(e, "profile")}
          />
          <input
            className="hidden-file-input"
            type="file"
            ref={brokerLogoFileRef}
            onChange={(e) => onFileChange(e, "logo")}
          />
          {<BCDashboard
            DashboardLayout={profileLayout}
            isGreaterThanMobile={isGreaterThanMobile}
            customTitleClass="profile-card-title"
            brokerColor={brokerColor}
          />}
        </React.Fragment>}
      </div>}
    </div>
  );
};

export default BCProfile;